import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { MastraRuntimeProvider } from '../../../home/providers/mastra-runtime-provider';
import { useThreads } from '../../../home/hooks/use-memory';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useMessages } from '../../../home/hooks/use-memory';
import { useParams } from 'react-router-dom';
import { useMemory } from '../../../home/hooks/use-memory';
import { useAgent } from '../../../home/hooks/use-agents';
import { useAuth } from '../../../../providers/authentication-provider';
import { HAS_VISITED_AGENT_PAGE_KEY } from './action-chat';
import { v4 as uuid } from '@lukeed/uuid';
import dayjs from 'dayjs';
import { AndsendModel } from '@andsend/services-agent-shared';

export const ActionChatRuntimeProvider: FC<PropsWithChildren> = ({ children }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const threadId = searchParams.get('threadId') || undefined;
    const [agentId, setAgentId] = useState<string>('actionFeedAgent');
    const [selectedModel, setSelectedModel] = useState<AndsendModel>(AndsendModel.GEMINI_2_0_FLASH);
    const { agent, mastra, isLoading: isAgentLoading, availableAgents } = useAgent(agentId, selectedModel);
    const { memory } = useMemory(agentId);
    const location = useLocation();
    const navigate = useNavigate();
    const { messages, isLoading: isMessagesLoading } = useMessages({
        agentId: agentId!,
        threadId: threadId!,
        memory: !!memory?.result,
    });
    const {
        authState: { userId },
    } = useAuth();
    const {
        threads,
        isLoading: isThreadsLoading,
        mutate: refreshThreads,
    } = useThreads({ resourceid: userId!, agentId: agentId!, isMemoryEnabled: !!memory?.result });
    const [hasWelcomeRoot, setHasWelcomeRoot] = useState(false);

    // Set localStorage flag when component mounts
    useEffect(() => {
        localStorage.setItem(HAS_VISITED_AGENT_PAGE_KEY, 'true');
    }, []);

    useEffect(() => {
        if (memory?.result && !threadId) {
            if (isThreadsLoading) {
                return;
            }

            let newThreadId = uuid();
            let existingThread = false;

            if (threads.length > 0) {
                const lastThread = threads?.sort(
                    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                )[0];
                // If last thread is created last 24 hours, use that thread;
                if (lastThread.createdAt && dayjs(lastThread.createdAt).isAfter(dayjs().subtract(1, 'day'))) {
                    newThreadId = lastThread.id;
                    existingThread = true;
                }
            }

            setSearchParams({ threadId: newThreadId });

            // use @lukeed/uuid because we don't need a cryptographically secure uuid (this is a debugging local uuid)
            // using crypto.randomUUID() on a domain without https (ex a local domain like local.lan:4111) will cause a TypeError
        }
    }, [memory?.result, threadId, threads, isThreadsLoading]);
    return (
        <MastraRuntimeProvider
            agentId={agentId}
            agentName={'Action Feed Agent'}
            threadId={threadId}
            initialMessages={isMessagesLoading ? undefined : messages}
            memory={memory?.result}
            mastra={mastra}
            refreshThreadList={() => {
                refreshThreads();
            }}
            modelSettings={{ maxSteps: 10, selectedModel, temperature: 0.4, topP: 0.9 }}
        >
            {children}
        </MastraRuntimeProvider>
    );
};
